// UNUSED BUT NEEDED FOR FUTURE USE
"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { sessionLogger } from "../utils/logger";
import { clientEnv } from "../../lib/env";
import { isZapierDomain } from "../utils/isZapierDomain";

export interface User {
  id: string;
  name: string;
  email: string;
}

interface UserSessionContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  clearSession: () => void;
}

const UserSessionContext = createContext<UserSessionContextType | undefined>(
  undefined,
);

interface UserSessionProviderProps {
  children: ReactNode;
}

export function UserSessionProvider({ children }: UserSessionProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentAccountId = useCurrentAccountId();

  const refreshUser = useCallback(async () => {
    // Skip session refresh if not on a Zapier domain to prevent CORS errors
    if (!isZapierDomain()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const zapierOrigin =
        clientEnv.NEXT_PUBLIC_ZAPIER_ORIGIN || "https://zapier.com";
      const sessionUrl = `${zapierOrigin}/api/v4/session`;

      const response = await fetch(sessionUrl, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else if (response.status === 401) {
        // User is not authenticated
        setUser(null);
        setError(null); // Clear any previous errors for 401
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      sessionLogger.error("failed_to_refresh_user_session", {
        error: err instanceof Error ? err.message : String(err),
      });
      setError(
        err instanceof Error ? err.message : "Failed to load user session",
      );
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setError(null);
  }, []);

  // Refresh user when currentAccountId changes
  useEffect(() => {
    if (currentAccountId !== undefined && currentAccountId !== null) {
      refreshUser();
    } else {
      setUser(null);
      setIsLoading(false);
    }
  }, [currentAccountId, refreshUser]);

  // Initial load
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value: UserSessionContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    refreshUser,
    clearSession,
  };

  return (
    <UserSessionContext.Provider value={value}>
      {children}
    </UserSessionContext.Provider>
  );
}

export function useUserSession() {
  const context = useContext(UserSessionContext);
  if (!context) {
    throw new Error("useUserSession must be used within UserSessionProvider");
  }
  return context;
}

// Convenience hook for checking authentication status
export function useIsAuthenticated() {
  const { isAuthenticated, isLoading } = useUserSession();
  return { isAuthenticated, isLoading };
}

// Convenience hook for getting current user
export function useCurrentUser() {
  const { user, isLoading, error } = useUserSession();
  return { user, isLoading, error };
}
